import { makeAutoObservable } from "mobx";
import { TIMELINE_RULER_TICK_WIDTH_PX } from "../config/config";

const MIN_ZOOM_PERCENT = 50;
const MAX_ZOOM_PERCENT = 1000;

class TimelineStoreInstance {
  width = 0;
  tracksContainerWidth = 0;
  draggedClips: Record<string, true> = {};
  draggedLibrettoClips: Record<string, true> = {};
  draggedTransitions: Record<string, true> = {};
  // User-facing zoom control (50%..1000%). Actual `zoom` (px scale factor) is derived elsewhere.
  zoomPercent = 50;
  didApplyDefaultZoom = false;
  zoom = 1;
  leftPadSeconds = 0.5;
  // Virtualization: time at x=0 of the track content (after left pad).
  baseStartSec = 0;
  // Latest scrollLeft of the tracks container (includes left pad).
  scrollLeftPx = 0;

  constructor() {
    makeAutoObservable(this, {}, { deep: true });
  }

  setWidth(width: number) {
    this.width = width;
  }

  setTracksContainerWidth(width: number) {
    this.tracksContainerWidth = width;
  }

  setScrollLeftPx(px: number) {
    this.scrollLeftPx = Math.max(0, Number.isFinite(px) ? px : 0);
  }

  setBaseStartSec(sec: number) {
    this.baseStartSec = Math.max(0, Number.isFinite(sec) ? sec : 0);
  }

  setDraggingClip(clipId: string, isDragging: boolean) {
    if (isDragging) {
      this.draggedClips[clipId] = true;
    } else {
      delete this.draggedClips[clipId];
    }
  }

  // setDraggingLibrettoClip(librettoClipId: string, isDragging: boolean) {
  //   if (isDragging) {
  //     this.draggedLibrettoClips[librettoClipId] = true;
  //     // Also mark all associated rendley clips as dragging for backward compatibility
  //     const clipIds = RendleyStore.getClipIdsForLibrettoClip(librettoClipId);
  //     clipIds.forEach(clipId => {
  //       this.draggedClips[clipId] = true;
  //     });
  //   } else {
  //     delete this.draggedLibrettoClips[librettoClipId];
  //     // Remove all associated rendley clips from dragging
  //     const clipIds = RendleyStore.getClipIdsForLibrettoClip(librettoClipId);
  //     clipIds.forEach(clipId => {
  //       delete this.draggedClips[clipId];
  //     });
  //   }
  // }

  setDraggingTransition(transitionId: string, isDragging: boolean) {
    if (isDragging) {
      this.draggedTransitions[transitionId] = true;
    } else {
      delete this.draggedTransitions[transitionId];
    }
  }

  setZoomPercent(percent: number) {
    const next = Math.max(MIN_ZOOM_PERCENT, Math.min(MAX_ZOOM_PERCENT, Math.round(percent)));
    this.zoomPercent = next;
  }

  setDidApplyDefaultZoom(applied: boolean) {
    this.didApplyDefaultZoom = Boolean(applied);
  }

  setZoom(zoom: number) {
    const next = Number.isFinite(zoom) ? zoom : this.zoom;
    // Timeline virtualization allows extremely large zoom factors; keep this bounded to avoid Infinity.
    this.zoom = Math.max(1e-6, Math.min(next, 1e6));
  }

  setLeftPadSeconds(seconds: number) {
    this.leftPadSeconds = Math.max(0, Number.isFinite(seconds) ? seconds : 0);
  }

  get pxPerSec(): number {
    return TIMELINE_RULER_TICK_WIDTH_PX * this.zoom;
  }

  get leftPadPx(): number {
    return (this.leftPadSeconds ?? 0) * this.pxPerSec;
  }

  reset() {
    this.width = 0;
    this.tracksContainerWidth = 0;
    this.draggedClips = {};
    this.draggedLibrettoClips = {};
    this.draggedTransitions = {};
    this.zoomPercent = 50;
    this.didApplyDefaultZoom = false;
    this.zoom = 1;
    this.baseStartSec = 0;
    this.scrollLeftPx = 0;
  }
}

const TimelineStore = new TimelineStoreInstance();

export { TimelineStore };
